import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  DocumentTextIcon,
  ArrowDownTrayIcon,
  EyeIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
} from '@heroicons/react/24/outline';
import { useWallet } from '../../context/WalletContext';
import { getUserInvoices } from '../../services/invoiceService';
import type { InvoiceListItem } from '../../types/invoice';

const InvoicesPage: React.FC = () => {
  const { address } = useWallet();
  const [invoices, setInvoices] = useState<InvoiceListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('date_desc');

  useEffect(() => {
    const fetchInvoices = async () => {
      if (address) {
        const data = await getUserInvoices(address);
        setInvoices(data);
      }
      setLoading(false);
    };
    fetchInvoices();
  }, [address]);

  const formatDate = (date: Date) => {
    return new Intl.DateTimeFormat('es-ES', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    }).format(date);
  };

  const filteredInvoices = invoices
    .filter(inv =>
      inv.itemName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      inv.invoiceNumber.toLowerCase().includes(searchQuery.toLowerCase())
    )
    .sort((a, b) => {
      if (sortBy === 'date_desc') return b.purchaseDate.getTime() - a.purchaseDate.getTime();
      if (sortBy === 'date_asc') return a.purchaseDate.getTime() - b.purchaseDate.getTime();
      if (sortBy === 'amount_desc') return b.priceCrypto - a.priceCrypto;
      if (sortBy === 'amount_asc') return a.priceCrypto - b.priceCrypto;
      return 0;
    });

  const totalAmount = invoices.reduce((sum, inv) => sum + inv.priceCrypto, 0);
  const totalEur = invoices.reduce((sum, inv) => sum + inv.priceEur, 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-primary-500/30 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-white mb-2 flex items-center gap-3">
            <DocumentTextIcon className="w-8 h-8 text-primary-400" />
            Mis Facturas
          </h1>
          <p className="text-gray-400">Historial de todas tus compras con facturas descargables</p>
        </div>

        {/* Stats */}
        <div className="flex gap-4">
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Total Facturas</p>
            <p className="text-xl font-bold text-white">{invoices.length}</p>
          </div>
          <div className="bg-dark-800/50 rounded-lg px-4 py-2 border border-dark-700">
            <p className="text-xs text-gray-500">Total Gastado</p>
            <p className="text-xl font-bold text-primary-400">{totalAmount.toFixed(2)} ETH</p>
            <p className="text-xs text-gray-500">~€{totalEur.toLocaleString()}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-col md:flex-row gap-4 mb-6">
        <div className="flex-1 relative">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
          <input
            type="text"
            placeholder="Buscar por nombre o número de factura..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
          />
        </div>
        <select
          value={sortBy}
          onChange={(e) => setSortBy(e.target.value)}
          className="px-4 py-2 bg-dark-800 border border-dark-700 rounded-lg text-white focus:outline-none focus:border-primary-500"
        >
          <option value="date_desc">Más recientes</option>
          <option value="date_asc">Más antiguas</option>
          <option value="amount_desc">Mayor importe</option>
          <option value="amount_asc">Menor importe</option>
        </select>
      </div>

      {/* Invoices List */}
      {filteredInvoices.length > 0 ? (
        <div className="bg-dark-800/50 rounded-xl border border-dark-700 overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="border-b border-dark-700">
                <th className="text-left px-6 py-4 text-gray-400 text-sm font-medium">Factura</th>
                <th className="text-left px-6 py-4 text-gray-400 text-sm font-medium">Item</th>
                <th className="text-right px-6 py-4 text-gray-400 text-sm font-medium">Importe</th>
                <th className="text-right px-6 py-4 text-gray-400 text-sm font-medium">Fecha</th>
                <th className="text-right px-6 py-4 text-gray-400 text-sm font-medium">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredInvoices.map((invoice, idx) => (
                <motion.tr
                  key={invoice.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: idx * 0.05 }}
                  className="border-b border-dark-700 last:border-0 hover:bg-dark-700/30"
                >
                  <td className="px-6 py-4">
                    <span className="text-white font-mono text-sm">{invoice.invoiceNumber}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <img
                        src={invoice.itemImage}
                        alt={invoice.itemName}
                        className="w-10 h-10 rounded-lg object-cover"
                      />
                      <span className="text-white">{invoice.itemName}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <p className="text-white font-medium">{invoice.priceCrypto} {invoice.currency}</p>
                    <p className="text-gray-500 text-xs">~€{invoice.priceEur.toLocaleString()}</p>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-400">
                    {formatDate(invoice.purchaseDate)}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/dashboard/invoices/${invoice.id}`}
                        className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                        title="Ver factura"
                      >
                        <EyeIcon className="w-5 h-5 text-gray-400" />
                      </Link>
                      <button
                        className="p-2 hover:bg-dark-700 rounded-lg transition-colors"
                        title="Descargar PDF"
                      >
                        <ArrowDownTrayIcon className="w-5 h-5 text-primary-400" />
                      </button>
                    </div>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="text-center py-16">
          <DocumentTextIcon className="w-16 h-16 text-gray-700 mx-auto mb-4" />
          <h3 className="text-xl font-semibold text-white mb-2">
            {searchQuery ? 'No se encontraron facturas' : 'No tienes facturas'}
          </h3>
          <p className="text-gray-400 mb-6">
            {searchQuery
              ? 'Intenta con otros términos de búsqueda'
              : 'Cuando realices una compra, tu factura aparecerá aquí'}
          </p>
          {!searchQuery && (
            <Link
              to="/explore"
              className="px-6 py-2 bg-primary-500 hover:bg-primary-600 text-white rounded-lg transition-colors"
            >
              Explorar Subastas
            </Link>
          )}
        </div>
      )}

      {/* Info */}
      <div className="mt-8 bg-blue-500/10 border border-blue-500/30 rounded-xl p-6">
        <div className="flex items-start gap-4">
          <DocumentTextIcon className="w-6 h-6 text-blue-400 flex-shrink-0" />
          <div>
            <h3 className="text-white font-semibold mb-2">Información sobre Facturas</h3>
            <ul className="space-y-1 text-gray-400 text-sm">
              <li>• Las facturas se generan automáticamente después de cada compra</li>
              <li>• Incluyen el hash de transacción verificable en blockchain</li>
              <li>• Puedes descargarlas en PDF para tu contabilidad</li>
              <li>• Los datos son inmutables y verificables en Polygonscan</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

export default InvoicesPage;
